import { FC, Fragment, useCallback, useMemo, useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';

import { Box, Button, ExclamationCircleSolid, Flex, HStack, IconButton, rem, Stack, Text, XOutline } from '@bonusly/design-system';

import { AttrMap, CustomPropertyUserGroup, ruleSectionKeyToConditionAttr, useAudienceGroupData } from '../../queries/groups';
import { GroupType } from '../../schemas/groups';
import { titleCase } from '../../utils/string';
import { Reveal } from '../display/reveal';
import { RadioChip, RadioChipGroup } from '../form/radio-chip';
import { AudienceFiltersMenu, includesTeams, InternalFilters } from './audience-filters-menu';
import { AudienceCondition } from './condition-audience-selector';
import { EligibilityCount } from './eligibility-count';

export type { AudienceCondition } from './condition-audience-selector';

type AudienceSelectorProps = {
	isDisabled?: boolean;
	onChange: (value: AudienceCondition[]) => void;
	excludeConditions?: AudienceCondition[];
	onExcludeChange?: (excludeConditions: AudienceCondition[]) => void;
	value: AudienceCondition[];
	operator?: GroupType;
	onOperatorChange?: (operator: GroupType) => void;
	includedUserIds?: string[];
	excludedUserIds?: string[];
	error?: string;
	/**
	 * When true, the include/exclude UI is always shown and audience is always defined.
	 * When false (default), everyone is eligible until the user clicks "Select audience".
	 */
	required?: boolean;
	translationNamespace?: string;
	translationKeyPrefix?: string;
	countMessageKey?: string;
	/**
	 * Explicit allow-list of section keys shown in the filter menu. When omitted, all groups are
	 * selectable. E.g. pass `['departments', 'locations', 'teams']` to only allow those three.
	 * Including `'teams'` also merges in manager teams from the user_groups endpoint (which the
	 * custom_property_groups source omits), so they appear in the menu and team chips resolve names.
	 */
	allowedSectionKeys?: readonly string[];
	hideExcludeSection?: boolean;
	/**
	 * When true, a "Custom Group" section is appended to the bottom of the filter menu, listing the
	 * company's admin-created custom groups (UserGroupQuery). Selecting one adds a
	 * `{ attr: 'custom_group', value: <userGroupQueryId> }` condition. Defaults to false; leave it
	 * off for the custom-group creation form so a group can't reference other custom groups.
	 */
	includeCustomGroups?: boolean;
};

// Caps the include field at ~5.5 chip rows so its obvious you can scroll to see more
const INCLUDE_FIELD_MAX_HEIGHT = rem(184);

const SCROLLBAR_STYLES = {
	'&::-webkit-scrollbar': { width: '0.5rem' },
	'&::-webkit-scrollbar-track': { background: 'transparent', margin: '0.5rem 0' },
	'&::-webkit-scrollbar-thumb': { background: 'scrollbar.thumb', borderRadius: 'full' },
	'&::-webkit-scrollbar-thumb:hover': { background: 'scrollbar.thumbHover' },
};

const EMPTY_CONDITIONS: AudienceCondition[] = [{ attr: undefined, value: undefined }];
const EMPTY_EXCLUDES: AudienceCondition[] = [];
const noop = () => {};

const getActiveConditions = (conditions: AudienceCondition[]) => conditions.filter((c) => c.attr && c.value) as { attr: string; value: string }[];

export const AudienceSelector: FC<AudienceSelectorProps> = ({
	isDisabled = false,
	onChange,
	excludeConditions = EMPTY_EXCLUDES,
	onExcludeChange = noop,
	value,
	operator,
	onOperatorChange,
	includedUserIds = [],
	excludedUserIds = [],
	error,
	required = false,
	translationNamespace = 'nominations',
	translationKeyPrefix = 'form',
	countMessageKey,
	allowedSectionKeys,
	hideExcludeSection = false,
	includeCustomGroups = false,
}) => {
	const { t } = useTranslation(translationNamespace, { keyPrefix: translationKeyPrefix });
	const { t: tShared } = useTranslation('common', { keyPrefix: 'audienceSelector' });
	const { data: groupData } = useAudienceGroupData(includesTeams(allowedSectionKeys));
	const [isExpanded, setIsExpanded] = useState(false);

	const nameLookup = useMemo(() => {
		if (!groupData) return new Map<string, string>();
		const lookup = new Map<string, string>();
		for (const [propertyKey, groups] of Object.entries(groupData)) {
			for (const group of groups as CustomPropertyUserGroup[]) {
				lookup.set(`${propertyKey}:${group.key}`, group.name);
			}
		}
		return lookup;
	}, [groupData]);

	const activeIncludes = useMemo(() => getActiveConditions(value), [value]);
	const activeExcludes = useMemo(() => getActiveConditions(excludeConditions), [excludeConditions]);

	const hasIncludes = activeIncludes.length > 0;
	const hasExcludes = activeExcludes.length > 0;
	const hasAnyFilters = hasIncludes || hasExcludes;
	const showFilters = required || isExpanded || hasAnyFilters;
	const showOperatorToggle = activeIncludes.length >= 2 && operator !== undefined && onOperatorChange !== undefined;

	const handleIncludeApply = useCallback(
		(filters: InternalFilters) => {
			const newConditions: AudienceCondition[] = [];
			for (const [sectionKey, values] of Object.entries(filters)) {
				const attr = ruleSectionKeyToConditionAttr(sectionKey);
				for (const val of values) {
					newConditions.push({ attr, value: val });
				}
			}
			onChange(newConditions.length > 0 ? newConditions : EMPTY_CONDITIONS);
		},
		[onChange]
	);

	const handleExcludeApply = useCallback(
		(filters: InternalFilters) => {
			const newConditions: AudienceCondition[] = [];
			for (const [sectionKey, values] of Object.entries(filters)) {
				const attr = ruleSectionKeyToConditionAttr(sectionKey);
				for (const val of values) {
					newConditions.push({ attr, value: val });
				}
			}
			onExcludeChange(newConditions);
		},
		[onExcludeChange]
	);

	const handleRemoveInclude = useCallback(
		(attr: string, val: string) => {
			const remaining = value.filter((c) => !(c.attr === attr && c.value === val));
			onChange(remaining.length > 0 ? remaining : EMPTY_CONDITIONS);
		},
		[onChange, value]
	);

	const handleRemoveExclude = useCallback(
		(attr: string, val: string) => {
			const remaining = excludeConditions.filter((c) => !(c.attr === attr && c.value === val));
			onExcludeChange(remaining);
		},
		[excludeConditions, onExcludeChange]
	);

	const handleClearAll = useCallback(() => {
		if (required) {
			onChange(EMPTY_CONDITIONS);
		} else {
			onChange([]);
			setIsExpanded(false);
		}
		onExcludeChange([]);
	}, [onChange, onExcludeChange, required]);

	const handleEnableAudience = useCallback(() => {
		setIsExpanded(true);
	}, []);

	const getDisplayName = (attr: string, val: string): string => {
		const sectionKey = AttrMap[attr] ?? attr;
		const rawName = nameLookup.get(`${sectionKey}:${val}`) ?? val;
		if (attr === 'department' || attr === 'location') {
			return titleCase(rawName);
		}
		return rawName;
	};

	const operatorWord = operator === GroupType.Or ? tShared('operatorOr') : tShared('operatorAnd');
	const conditionNames = activeIncludes.map((c) => getDisplayName(c.attr, c.value));

	if (!showFilters) {
		return (
			<Button size="sm" variant="alpha" alignSelf="flex-start" isDisabled={isDisabled} onClick={handleEnableAudience} data-testid="filter-audience-button">
				{tShared('selectAudienceButton')}
			</Button>
		);
	}

	return (
		<Stack spacing={3} opacity={isDisabled ? 0.7 : undefined} pointerEvents={isDisabled ? 'none' : undefined}>
			<EligibilityCount
				conditions={value}
				excludeConditions={excludeConditions}
				operator={operator ?? GroupType.And}
				includedUserIds={includedUserIds}
				excludedUserIds={excludedUserIds}
				translationNamespace={translationNamespace}
				translationKeyPrefix={translationKeyPrefix}
				countMessageKey={countMessageKey}
				mb={0}
			/>

			{/* Include field */}
			<Flex
				border={error ? '2px solid' : '1px solid'}
				borderColor={error ? 'error.inputBorder' : 'border.default'}
				borderRadius="lg"
				minH={10}
				maxH={INCLUDE_FIELD_MAX_HEIGHT}
				overflowY="auto"
				sx={SCROLLBAR_STYLES}
				flexWrap="wrap"
				align="center"
				p={1}
				gap={0}
			>
				<HStack spacing={1} px={3} h={8} align="center" borderRight="1px solid" borderColor="border.default" mr={1}>
					<Text fontSize={100} fontWeight="medium" color="text.default" whiteSpace="nowrap">
						{tShared('includeLabel')}
					</Text>
				</HStack>

				{!hasIncludes && (
					<Flex align="center" h={8} px={2} flex={1}>
						<Text fontSize={100} color="text.softest">
							{tShared('allUsersPlaceholder')}
						</Text>
					</Flex>
				)}

				{activeIncludes.map((condition) => (
					<Box key={`inc-${condition.attr}-${condition.value}`} py={0.5} ml={1}>
						<HStack spacing={1} align="center" bgColor="primary.bgAlpha" borderRadius="md" pl={3} pr={1} h={7}>
							<Text color="primary.fgStrong" fontSize={50} fontWeight="medium">
								{getDisplayName(condition.attr, condition.value)}
							</Text>
							<IconButton
								aria-label={tShared('filterRemoveFilter', { filterType: getDisplayName(condition.attr, condition.value) })}
								icon={<XOutline size="xs" />}
								variant="ghost"
								size="xs"
								minW={5}
								h={5}
								isDisabled={isDisabled}
								onClick={() => handleRemoveInclude(condition.attr, condition.value)}
								color="primary.fgStrong"
								_hover={{ bgColor: 'primary.bgAlphaHover' }}
							/>
						</HStack>
					</Box>
				))}

				<Box ml={hasIncludes ? 1 : 0} pr={hasIncludes ? 0 : 2}>
					<AudienceFiltersMenu
						currentConditions={value}
						hasFilters={hasIncludes}
						onApply={handleIncludeApply}
						isDisabled={isDisabled}
						allowedSectionKeys={allowedSectionKeys}
						includeCustomGroups={includeCustomGroups}
					/>
				</Box>
			</Flex>

			{/* And/Or toggle directly under Include */}
			<Reveal if={showOperatorToggle}>
				<HStack spacing={3} align="flex-start">
					<RadioChipGroup
						isDisabled={isDisabled}
						onChange={(val: string) => onOperatorChange?.(val as GroupType)}
						value={operator}
						borderColor="border.default"
					>
						<RadioChip value={GroupType.And}>{t('audienceAnd')}</RadioChip>
						<RadioChip value={GroupType.Or}>{t('audienceOr')}</RadioChip>
					</RadioChipGroup>
					<Text fontSize={50} color="text.soft" pt={1.5}>
						{conditionNames.map((name, i) => (
							<Fragment key={i}>
								{i > 0 && (
									<>
										{' '}
										<strong>{operatorWord}</strong>{' '}
									</>
								)}
								{name}
							</Fragment>
						))}
						{'. '}
						<Trans t={tShared} i18nKey={operator === GroupType.Or ? 'operatorDescriptionOr' : 'operatorDescriptionAnd'} components={{ bold: <strong /> }} />
					</Text>
				</HStack>
			</Reveal>

			{/* Exclude field */}
			<Reveal if={!hideExcludeSection}>
				<Flex
					border={error ? '2px solid' : '1px solid'}
					borderColor={error ? 'error.inputBorder' : 'border.default'}
					borderRadius="lg"
					minH={10}
					flexWrap="wrap"
					align="center"
					p={1}
					gap={0}
				>
					<HStack spacing={1} px={3} h={8} align="center" borderRight="1px solid" borderColor="border.default" mr={1}>
						<Text fontSize={100} fontWeight="medium" color="text.default" whiteSpace="nowrap">
							{tShared('excludeLabel')}
						</Text>
					</HStack>

					{!hasExcludes && (
						<Flex align="center" h={8} px={2} flex={1}>
							<Text fontSize={100} color="text.softest">
								{tShared('noExclusionsPlaceholder')}
							</Text>
						</Flex>
					)}

					{activeExcludes.map((condition) => (
						<Box key={`exc-${condition.attr}-${condition.value}`} py={0.5} ml={1}>
							<HStack spacing={1} align="center" bgColor="danger.bgAlpha" borderRadius="md" pl={3} pr={1} h={7}>
								<Text color="danger.fgStrong" fontSize={50} fontWeight="medium">
									{getDisplayName(condition.attr, condition.value)}
								</Text>
								<IconButton
									aria-label={tShared('excludeRemoveFilter', { filterType: getDisplayName(condition.attr, condition.value) })}
									icon={<XOutline size="xs" />}
									variant="ghost"
									size="xs"
									minW={5}
									h={5}
									isDisabled={isDisabled}
									onClick={() => handleRemoveExclude(condition.attr, condition.value)}
									color="danger.fgStrong"
									_hover={{ bgColor: 'danger.bgAlphaHover' }}
								/>
							</HStack>
						</Box>
					))}

					<Box ml={hasExcludes ? 1 : 0} pr={hasExcludes ? 0 : 2}>
						<AudienceFiltersMenu
							currentConditions={excludeConditions}
							hasFilters={hasExcludes}
							onApply={handleExcludeApply}
							variant="exclude"
							isDisabled={isDisabled}
							allowedSectionKeys={allowedSectionKeys}
							includeCustomGroups={includeCustomGroups}
						/>
					</Box>
				</Flex>
			</Reveal>

			<Reveal if={!!error}>
				<Flex align="center">
					<ExclamationCircleSolid color="error.text" size="sm" mr={1} />
					<Text color="error.text" size="sm">
						{error}
					</Text>
				</Flex>
			</Reveal>

			<Reveal if={hasAnyFilters}>
				<Button variant="link" colorScheme="secondary" size="sm" isDisabled={isDisabled} onClick={handleClearAll} alignSelf="flex-start">
					{tShared('clearAllLink')}
				</Button>
			</Reveal>
		</Stack>
	);
};
